import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { To } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconAngleDown } from '@sorare/core/src/atoms/icons/system/iconAngleDown.ts';
import { iconAngleUp } from '@sorare/core/src/atoms/icons/system/iconAngleUp.ts';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { uniqueBy } from '@sorare/core/src/lib/arrays';

import type { ChampionshipCompetitionSelect_so5UserGroup } from './__generated__/index.graphql.ts';

const Floating = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: calc(100% + var(--unit));
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  padding: var(--intermediate-unit) var(--double-unit);
  background: var(--c-black);
`;
const Option = styled(SBHorizontal)`
  cursor: pointer;
  gap: var(--unit);
  min-width: 320px;
  padding: var(--intermediate-unit) var(--double-unit);
  transition: 0.2s ease-in-out;
  .composable &:hover {
    background-color: var(--c-nd-50);
    + ${Floating} {
      display: initial;
    }
  }
`;
const OptionContainer = styled.div`
  position: relative;
  &:not(.composable) {
    opacity: 0.5;
    &:hover {
      opacity: 0.65;
    }
  }
`;

type Props = {
  selectUserGroupSlug: string;
  userGroups: ChampionshipCompetitionSelect_so5UserGroup[];
  to: (args: { competition: string; userGroup: string; user: string }) => To;
  allowNavigatingWhenLocked?: boolean;
};

export const ChampionshipCompetitionSelect = ({
  selectUserGroupSlug,
  userGroups,
  to: toProp,
  allowNavigatingWhenLocked = true,
}: Props) => {
  const dedupedUserGroups = uniqueBy(g => g.slug, userGroups);
  const selectSo5UserGroup =
    dedupedUserGroups.find(({ slug }) => selectUserGroupSlug === slug) ||
    dedupedUserGroups[0];

  const rarity = selectSo5UserGroup?.mainRarityType as Rarity;

  const so5UserGroupsMatchingRarity = rarity
    ? dedupedUserGroups.filter(
        ({ mainRarityType }) => mainRarityType === rarity
      )
    : dedupedUserGroups;

  if (!selectSo5UserGroup || so5UserGroupsMatchingRarity.length <= 1) {
    return null;
  }

  return (
    <Dropdown
      gap={4}
      label={({ open, ...btnProps }) => (
        <Button
          disableDebounce
          color="tertiary"
          size="small"
          endIcon={
            <FontAwesomeIcon icon={open ? iconAngleUp : iconAngleDown} />
          }
          className={classNames({ active: open })}
          {...btnProps}
        >
          <LabelL>{selectSo5UserGroup.displayName}</LabelL>
        </Button>
      )}
    >
      {({ closeDropdown }) => (
        <>
          {so5UserGroupsMatchingRarity.map(userGroup => {
            const to = toProp({
              competition: userGroup.slug,
              userGroup: userGroup.slug,
              user: '',
            });

            return (
              <OptionContainer
                key={userGroup.slug}
                className={classNames({
                  composable: true,
                })}
              >
                <Option
                  {...(allowNavigatingWhenLocked
                    ? {
                        as: Link,
                        to,
                        replace: true,
                        onClick: closeDropdown,
                      }
                    : {})}
                >
                  <Horizontal>
                    <LabelL
                      color={
                        selectUserGroupSlug === userGroup.slug
                          ? 'var(--c-white)'
                          : 'var(--c-nd-600)'
                      }
                    >
                      {userGroup.displayName}
                    </LabelL>
                  </Horizontal>
                </Option>
              </OptionContainer>
            );
          })}
        </>
      )}
    </Dropdown>
  );
};

ChampionshipCompetitionSelect.fragments = {
  so5UserGroup: gql`
    fragment ChampionshipCompetitionSelect_so5UserGroup on So5UserGroup {
      slug
      displayName
      mainRarityType
    }
  ` as TypedDocumentNode<ChampionshipCompetitionSelect_so5UserGroup>,
};
